import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import "../components/index.js";
import { toast } from "../components/index.js";
import "./stepup-dialog.js";
import {
  getAccountDeletion,
  exportAccount,
  deleteAccount,
  type AccountDeletion,
  type ErasureResult,
} from "../api/account.js";
import { signOut } from "../api/auth.js";
import { humanError, parseError } from "../api/errors.js";
import { navigate } from "../nav.js";

/** The two actions this page spends a step-up grant on. */
type AccountAction = "export_account" | "delete_account";

/** BlockingTeam is a team with other members that blocks the deletion: a
 * shared team the caller alone owns, or the personal team. The preflight knows
 * its member count; a 409 `sole_owner` body names only id, name and personal. */
interface BlockingTeam {
  id: string;
  name: string;
  personal: boolean;
  members?: number;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * page-account (/account) is where a user takes their data out or deletes
 * their account. Both need a fresh step-up grant (stepup-dialog). The routes
 * exist only while the server has erasure on, so the page first loads the
 * deletion preflight (GET /v1/account/deletion); when that route is absent it
 * shows a short notice and nothing else.
 *
 * The preflight also says what a deletion would do: teams with no other member
 * (the personal team among them) are deleted with the account, the other teams
 * are left, and a shared team the user alone owns blocks the deletion until
 * another member becomes an owner. The personal team goes with the account, so
 * while it has other members it blocks the deletion too, until they are
 * removed. The server checks all of this again when the deletion runs; if the
 * teams changed in between, its 409 `sole_owner` names the blocking teams and
 * the page lists those instead.
 *
 * A deletion ends the session. The final dialog therefore has one way out:
 * it signs out and goes to /login.
 */
@customElement("page-account")
export class PageAccount extends LitElement {
  @state() private loading = true;
  @state() private loadError = "";
  /** The preflight, or null when the server has erasure off. */
  @state() private deletion: AccountDeletion | null = null;
  @state() private blockers: BlockingTeam[] = [];

  /** The action the step-up dialog is gating (null = closed). */
  @state() private verifyFor: AccountAction | null = null;
  @state() private exporting = false;

  @state() private confirmOpen = false;
  @state() private deleting = false;
  /** The erasure answer once the account is gone; it opens the final dialog. */
  @state() private deleted: ErasureResult | null = null;

  static override styles = css`
    :host {
      display: block;
    }
    .head {
      margin-bottom: var(--bino-space-lg);
    }
    h1 {
      margin: 0 0 var(--bino-space-xs);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
      color: var(--bino-text);
    }
    .stack {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-lg);
    }
    .load-error {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--bino-space-md);
      padding: var(--bino-space-xl) var(--bino-space-lg);
      text-align: center;
      font-family: var(--bino-font-sans);
      color: var(--bino-text-secondary);
    }
    .card-copy,
    .modal-copy {
      margin: 0 0 var(--bino-space-md);
      max-width: 60ch;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
    }
    .modal-copy:last-child {
      margin-bottom: 0;
    }
    .teams {
      margin: 0 0 var(--bino-space-md);
      padding-left: 1.25rem;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
    }
    .teams li {
      margin-bottom: var(--bino-space-xs);
    }
    .teams .count {
      color: var(--bino-text-secondary);
    }
    .teams a {
      color: var(--bino-text);
    }
    .warn {
      margin: 0 0 var(--bino-space-md);
      padding: var(--bino-space-md);
      background: var(--bino-warning-bg);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-warning-text);
    }
    .warn p {
      margin: 0 0 var(--bino-space-sm);
    }
    .warn .teams {
      margin-bottom: var(--bino-space-sm);
      color: var(--bino-warning-text);
    }
    .warn .teams:last-child {
      margin-bottom: 0;
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  private async load() {
    this.loading = true;
    this.loadError = "";
    try {
      this.deletion = await getAccountDeletion();
      this.blockers = this.deletion?.soleOwnerTeams ?? [];
    } catch (e) {
      this.loadError = humanError(e);
    } finally {
      this.loading = false;
    }
  }

  // ---- Step-up ---------------------------------------------------------------

  private onVerified(verification: string) {
    const action = this.verifyFor;
    this.verifyFor = null;
    if (action === "export_account") void this.runExport(verification);
    else if (action === "delete_account") void this.runDelete(verification);
  }

  /**
   * failed routes an action error: a missing, expired or spent grant reopens
   * the step-up dialog, so the user only has to verify again; anything else
   * just toasts.
   */
  private failed(e: unknown, action: AccountAction) {
    toast(humanError(e), "error");
    const { code } = parseError(e);
    if (code === "verification_required" || code === "verification_invalid") {
      this.verifyFor = action;
    }
  }

  // ---- Export ----------------------------------------------------------------

  private async runExport(verification: string) {
    this.exporting = true;
    try {
      const { blob, filename } = await exportAccount(verification);
      downloadBlob(blob, filename);
      toast("Your data was downloaded", "success");
    } catch (e) {
      this.failed(e, "export_account");
    } finally {
      this.exporting = false;
    }
  }

  // ---- Delete ----------------------------------------------------------------

  private continueDelete() {
    this.confirmOpen = false;
    this.verifyFor = "delete_account";
  }

  private async runDelete(verification: string) {
    this.deleting = true;
    try {
      this.deleted = await deleteAccount(verification);
    } catch (e) {
      const err = parseError(e);
      if (err.code === "sole_owner") {
        toast(humanError(e), "error");
        this.blockers = err.teams ?? [];
      } else {
        this.failed(e, "delete_account");
      }
    } finally {
      this.deleting = false;
    }
  }

  /** finish leaves the dead session behind: the server revoked it with the account. */
  private finish() {
    this.deleted = null;
    signOut();
    navigate("/login");
  }

  // ---- Render ----------------------------------------------------------------

  private renderExport() {
    return html`<bn-card heading="Download your data">
      <p class="card-copy">
        One JSON file with your profile, your team memberships, the details of your API keys and
        passkeys (never the keys themselves), invitations sent to your email, and your own sign-in
        activity, IP addresses included.
      </p>
      <bn-button
        variant="primary"
        ?loading=${this.exporting}
        @click=${() => (this.verifyFor = "export_account")}
        >Download your data</bn-button
      >
    </bn-card>`;
  }

  private renderBlockerList(teams: BlockingTeam[]) {
    return html`<ul class="teams">
      ${teams.map(
        (t) => html`<li>
          <a href=${"/teams/" + encodeURIComponent(t.id)}>${t.name}</a>
          ${t.members !== undefined
            ? html`<span class="count">(${plural(t.members, "member", "members")})</span>`
            : nothing}
        </li>`,
      )}
    </ul>`;
  }

  private renderBlockers() {
    const shared = this.blockers.filter((t) => !t.personal);
    const personal = this.blockers.filter((t) => t.personal);
    return html`<div class="warn" role="alert">
      ${shared.length > 0
        ? html`<p>
              You are the only owner of these teams, and they have other members. Make another
              member an owner, or remove the other members, before you delete your account.
            </p>
            ${this.renderBlockerList(shared)}`
        : nothing}
      ${personal.length > 0
        ? html`<p>
              Your personal team has other members. It is deleted with your account, with all its
              workspaces, so remove the other members before you delete your account.
            </p>
            ${this.renderBlockerList(personal)}`
        : nothing}
    </div>`;
  }

  private renderDeleteEffects(d: AccountDeletion) {
    return html`<p class="card-copy">
        Your profile, passkeys, API keys, invitations sent to your email and your sign-in history
        are deleted.
      </p>
      ${d.deletesTeams.length > 0
        ? html`<p class="card-copy">
              These teams have no other members. They are deleted with all their workspaces:
            </p>
            <ul class="teams">
              ${d.deletesTeams.map(
                (t) => html`<li>
                  ${t.name}${t.personal ? " (personal)" : ""}
                  <span class="count">(${plural(t.workspaces, "workspace", "workspaces")})</span>
                </li>`,
              )}
            </ul>`
        : nothing}
      ${d.leavesTeams > 0
        ? html`<p class="card-copy">
            You leave ${plural(d.leavesTeams, "other team", "other teams")}. Their workspaces stay
            with the other members.
          </p>`
        : nothing}
      <p class="card-copy">
        This cannot be undone. Copies in backups are removed within 35 days.
      </p>`;
  }

  private renderDelete(d: AccountDeletion) {
    const blocked = this.blockers.length > 0;
    return html`<bn-card heading="Delete account">
      ${blocked ? this.renderBlockers() : this.renderDeleteEffects(d)}
      <bn-button
        variant="danger"
        ?disabled=${blocked}
        ?loading=${this.deleting}
        @click=${() => (this.confirmOpen = true)}
        >Delete account</bn-button
      >
    </bn-card>`;
  }

  private renderConfirmModal() {
    return html`<bn-modal
      ?open=${this.confirmOpen}
      heading="Delete your account?"
      @close=${() => (this.confirmOpen = false)}
    >
      <p class="modal-copy">
        Everything listed on this page is deleted for good, and you are signed out. Download your
        data first if you want to keep a copy.
      </p>
      <bn-button slot="footer" variant="ghost" @click=${() => (this.confirmOpen = false)}
        >Cancel</bn-button
      >
      <bn-button slot="footer" variant="danger" @click=${() => this.continueDelete()}
        >Delete account</bn-button
      >
    </bn-modal>`;
  }

  private renderDeletedModal() {
    const d = this.deleted;
    return html`<bn-modal
      ?open=${d !== null}
      heading="Your account was deleted"
      @close=${() => this.finish()}
    >
      <p class="modal-copy">
        ${d?.status === "deleting"
          ? "You can no longer sign in. The rest of your data is removed in the background within minutes."
          : "You can no longer sign in, and your data is gone."}
        Copies in backups are removed within 35 days.
      </p>
      <bn-button slot="footer" variant="primary" @click=${() => this.finish()}>Close</bn-button>
    </bn-modal>`;
  }

  override render() {
    if (this.loading) {
      return html`<bn-spinner block label="Loading account…"></bn-spinner>`;
    }
    if (this.loadError) {
      return html`<bn-card flush>
        <div class="load-error">
          <span>${this.loadError}</span>
          <bn-button variant="secondary" size="sm" @click=${() => void this.load()}
            >Retry</bn-button
          >
        </div>
      </bn-card>`;
    }
    const d = this.deletion;
    if (d === null) {
      return html`<bn-empty-state
        icon="∅"
        heading="Not available on this server"
        message="This server does not offer data export or account deletion."
      >
        <bn-button slot="actions" variant="secondary" @click=${() => navigate("/security")}
          >Back to security</bn-button
        >
      </bn-empty-state>`;
    }
    return html`
      <div class="head">
        <h1>Account</h1>
      </div>

      <div class="stack">${this.renderExport()} ${this.renderDelete(d)}</div>

      ${this.renderConfirmModal()} ${this.renderDeletedModal()}
      <stepup-dialog
        ?open=${this.verifyFor !== null}
        .action=${this.verifyFor ?? "export_account"}
        @verified=${(e: CustomEvent<{ verification: string }>) =>
          this.onVerified(e.detail.verification)}
        @close=${() => (this.verifyFor = null)}
      ></stepup-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-account": PageAccount;
  }
}
